// ============================================================================
// LANCEUR « APPLICATIONS » (barre du haut, maquette du 2026-10-26) — ouvre l'EDI et le store dans un nouvel onglet, par
// la connexion unique. Les lignes et leur état viennent de `staff-launcher.ts` ; ce fichier ne fait que les dessiner.
//
// ⚠️ RELU À CHAQUE OUVERTURE : la sonde bouge toutes les minutes, un état gardé depuis le chargement de la Console
//    afficherait une latence de la veille comme si elle était de maintenant.
// ⚠️ LE BOUTON EST CELUI DE LA BARRE (`TopbarAction`) et le panneau celui du dépôt (`Popover`) : clic extérieur, Échap
//    et focus rendu y sont déjà. Le voile du panneau empêche aussi la cloche de s'ouvrir par-dessus — les deux
//    déroulés ne sont jamais ouverts ensemble, comme le veut la maquette.
// ============================================================================
import { useEffect, useState } from 'react';
import { Icon, Popover, TopbarAction } from '@orgazone/ui-web';
import { api } from './api';
import { launcherRows, type LauncherRow, type LauncherSource } from './staff-launcher';
import { TOPBAR_PANEL } from './topbar-panel';

type State = { rows: LauncherRow[] | null; idpError: string | null; svcError: string | null };

const settle = <T,>(r: PromiseSettledResult<T>): LauncherSource<T> =>
  r.status === 'fulfilled' ? { ok: true, data: r.value } : { ok: false, error: r.reason instanceof Error ? r.reason.message : String(r.reason) };

/** Le contenu d'une ligne : pictogramme, nom, description, état de la sonde et adresse. */
function RowBody({ row }: { row: LauncherRow }) {
  const off = row.href === null;
  return (
    <>
      <span aria-hidden="true" style={{
        width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center', flexShrink: 0,
        background: off ? 'var(--ink100)' : 'var(--ok-bg)', color: off ? 'var(--ink500)' : 'var(--ok-fg)',
      }}>
        <Icon name={row.icon} size={17} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: off ? 'var(--ink700)' : 'var(--ink900)' }}>{row.name}</span>
        <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 1, lineHeight: 1.4 }}>{row.blocked ?? row.desc}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, minWidth: 0 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: row.health.dot }} />
          <span className="mono" data-health style={{ fontSize: 'var(--t-micro)', fontWeight: 600, whiteSpace: 'nowrap', color: row.health.fg }}>{row.health.label}</span>
          {row.host && (
            <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {row.host}</span>
          )}
        </span>
      </span>
      {!off && <span aria-hidden="true" style={{ color: 'var(--ink400)', flexShrink: 0, display: 'grid' }}><Icon name="external" size={14} /></span>}
    </>
  );
}

const rowSt = { display: 'flex', alignItems: 'center', gap: 11, padding: '9px 10px', borderRadius: 'var(--r-sm)' } as const;

export function AppLauncher() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<State | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setState(null);
    void Promise.allSettled([api.identityApps(), api.healthServices()]).then(([idpR, svcR]) => {
      if (!alive) return;
      const idp = settle(idpR);
      const svc = settle(svcR);
      setState({
        rows: launcherRows(idp, svc.ok ? { ok: true, data: svc.data.services } : svc),
        idpError: idp.ok ? null : idp.error,
        svcError: svc.ok ? null : svc.error,
      });
    });
    return () => { alive = false; };
  }, [open]);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      id="console-apps"
      label="Ouvrir une application"
      geom={TOPBAR_PANEL.geom(340)}
      style={TOPBAR_PANEL.style}
      trigger={(a) => (
        <TopbarAction
          icon="grid"
          label="Applications"
          onClick={a.onClick}
          trigger={{ ref: a.ref, 'aria-expanded': a['aria-expanded'], 'aria-haspopup': a['aria-haspopup'], 'aria-controls': a['aria-controls'] }}
        />
      )}
    >
      {({ close }) => (
        <>
          <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink500)', padding: '6px 10px 8px' }}>
            Ouvrir une application
          </div>
          {!state && <div className="muted" style={{ padding: '4px 10px 10px', fontSize: 'var(--t-body)' }}>Lecture…</div>}
          {state && state.rows === null && (
            <div role="alert" style={{ padding: '4px 10px 10px', fontSize: 'var(--t-dense)', color: 'var(--wa-fg)', lineHeight: 1.5 }}>
              L’IdP n’a pas répondu : {state.idpError}. Sans lui, aucune adresse n’est connue et la connexion unique ne peut ouvrir aucune application.
            </div>
          )}
          {state?.rows?.map((row) => (row.href ? (
            <a key={row.clientId} data-app={row.clientId} href={row.href} target="_blank" rel="noopener noreferrer" onClick={close}
              style={{ ...rowSt, color: 'inherit', textDecoration: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ink100)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <RowBody row={row} />
            </a>
          ) : (
            <div key={row.clientId} data-app={row.clientId} style={{ ...rowSt, opacity: 0.7 }}>
              <RowBody row={row} />
            </div>
          )))}
          {state?.svcError && (
            <div style={{ padding: '6px 10px 2px', fontSize: 'var(--t-meta)', color: 'var(--wa-fg)', lineHeight: 1.45 }}>
              Les sondes n’ont pas pu être lues : {state.svcError}
            </div>
          )}
          <div style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)', padding: '8px 10px 4px', borderTop: '1px solid var(--ink100)', marginTop: 6, lineHeight: 1.45 }}>
            Connexion unique : l’application s’ouvre dans un nouvel onglet, sans nouvel identifiant. Le back-office s’ouvre depuis chaque magasin : il porte les données d’un client.
          </div>
        </>
      )}
    </Popover>
  );
}
